import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";

import { ReactComponent as ChevronIcon } from "src/images/chevron-up-grey.svg";
import { renderMarkdown } from "../../utils/renderMarkdown";
import {
  formatThinkingContent,
  getLatestThinkingHeading,
  getLatestThinkingSection,
} from "../../utils/formatThinkingContent";
import "./ThinkingBlock.scss";

interface ThinkingBlockProps {
  content: string;
  seconds?: number;
  isStreaming: boolean;
  memberUuid?: string;
}

export default function ThinkingBlock({
  content,
  seconds,
  isStreaming,
  memberUuid,
}: ThinkingBlockProps) {
  // Open while streaming (to surface live thinking); collapse once it stops.
  const [isOpen, setIsOpen] = useState(isStreaming);
  const wasStreaming = useRef(isStreaming);

  useEffect(() => {
    if (wasStreaming.current && !isStreaming) setIsOpen(false);
    wasStreaming.current = isStreaming;
  }, [isStreaming]);

  const formatted = formatThinkingContent(content);

  // While streaming, the label is the heading of the section currently being
  // written (falling back to "Thinking..." before the first heading appears);
  // once finished it becomes the elapsed time.
  const label = isStreaming
    ? getLatestThinkingHeading(formatted) ?? "Thinking..."
    : `Thought for ${seconds ?? 0} second${seconds !== 1 ? "s" : ""}`;

  // While streaming show only the latest section's body (its heading is in the
  // label); once finished (or reopened) show the full thinking with headings.
  const displayContent = isStreaming
    ? getLatestThinkingSection(formatted)
    : formatted;

  return (
    <div
      className={classNames("thinking-block", {
        "thinking-block--open": isOpen,
        "thinking-block--streaming": isStreaming,
      })}
    >
      <button
        className="thinking-block__header"
        onClick={() => setIsOpen((v) => !v)}
        type="button"
      >
        <ChevronIcon className="thinking-block__chevron" />
        <span className="thinking-block__label">{label}</span>
      </button>
      {isOpen && (
        <div
          className="thinking-block__content"
          dangerouslySetInnerHTML={{
            __html: renderMarkdown(displayContent, memberUuid),
          }}
        />
      )}
    </div>
  );
}
